import React from 'react';
import PropTypes from 'prop-types';
import { Box, Group, Flex, IconButton, Table, Text } from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { ReactComponent as TrashOutlineIconSVG } from '../../../assets/trash-outline-icon.svg';
import { sortInAlphabeticalOrder } from '../../../functions';
import SelectField from '../../basic/SelectField';
import FastInput from '../../basic/FastInput';

const IssuesXcodesTableTd = ({ children, ...rest }) => {
  return (
    <Table.Cell paddingRight="16px" paddingLeft={0} paddingTop="8px" {...rest}>
      {children}
    </Table.Cell>
  );
};

IssuesXcodesTableTd.propTypes = {
  children: PropTypes.node.isRequired,
};

const columns = [
  {
    title: 'Category',
    dataIndex: 'category',
    isRequired: true,
  },
  {
    title: 'Reason',
    dataIndex: 'code',
    isRequired: true,
  },
  {
    title: 'Description',
    dataIndex: 'description',
  },
];

const IssuesXcodes = ({
  issuesXcodesOption,
  allIssuesXcodesOption,
  isInEditMode,
  issuesXcodes,
  onChange,
  onDelete,
  onAdd,
}) => {
  // filter issuesXcodesOption to get only unique categories
  const categoryOptions = issuesXcodesOption
    ?.filter((v, i, a) => a.findIndex(t => t.category === v.category) === i)
    ?.map(category => ({
      value: category.category,
      label: category.category,
    }))
    .sort((a, b) => {
      const optA = a.label.toLowerCase();
      const optB = b.label.toLowerCase();
      return sortInAlphabeticalOrder(optA, optB);
    });

  // filter issuesXcodesOption to get only unique codes and return value and label
  const getCodesByCategory = category => {
    return issuesXcodesOption
      .filter(issueXcode => issueXcode.category === category)
      .map(issueXcode => ({
        value: issueXcode.code,
        label: issueXcode.code,
      }))
      .sort((a, b) => {
        const optA = a.label.toLowerCase();
        const optB = b.label.toLowerCase();
        return sortInAlphabeticalOrder(optA, optB);
      });
  };

  const getFieldValue = (id, key) => {
    const issueXcode = allIssuesXcodesOption?.find(ix => ix?.id === id);
    return issueXcode ? issueXcode[key] : '';
  };

  return (
    <Table.Root variant="unstyled">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader width="10px" padding="0" paddingRight="26px" />
          {columns.map(column => (
            <Table.ColumnHeader
              key={column.dataIndex}
              paddingLeft={0}
              paddingBottom={0}
              paddingTop={0}
              textTransform="capitalize"
              fontSize="14px"
              fontWeight="500"
              fontFamily="var(--chakra-fonts-body)"
            >
              {column.title} {column.isRequired && isInEditMode ? ' *' : ''}
            </Table.ColumnHeader>
          ))}
          <Table.ColumnHeader />
        </Table.Row>
      </Table.Header>
      <Table.Body backgroundColor="white">
        {Array.isArray(issuesXcodes) &&
          issuesXcodes.length > 0 &&
          issuesXcodes.map((issueXcode, index) => (
            <Table.Row key={issueXcode.id || index}>
              <IssuesXcodesTableTd paddingLeft="1em">
                <Text color="accent.one.default" fontWeight="700">
                  {index + 1}
                </Text>
              </IssuesXcodesTableTd>
              <IssuesXcodesTableTd paddingLeft={0}>
                <Box minWidth="204px">
                  <SelectField
                    disabled={!isInEditMode}
                    isSearchable
                    closeMenuOnSelect
                    name="select-category"
                    defaultValue={getFieldValue(issueXcode.issue_xcode_id, 'category') || issueXcode.category}
                    onChange={e => onChange(index, 'category', e.value)}
                    options={categoryOptions}
                  />
                </Box>
              </IssuesXcodesTableTd>
              <IssuesXcodesTableTd paddingLeft={0}>
                <Box minWidth="204px">
                  <SelectField
                    disabled={!isInEditMode}
                    isSearchable
                    closeMenuOnSelect
                    name="select-code"
                    defaultValue={getFieldValue(issueXcode.issue_xcode_id, 'code')}
                    onChange={e => onChange(index, 'code', e.value)}
                    options={getCodesByCategory(
                      issueXcode.category || getFieldValue(issueXcode.issue_xcode_id, 'category')
                    )}
                  />
                </Box>
              </IssuesXcodesTableTd>
              <IssuesXcodesTableTd paddingLeft={0}>
                <FastInput
                  className="form-control"
                  value={issueXcode.description || ''}
                  disabled={!isInEditMode}
                  minWidth="210px"
                  placeholder="Add description"
                  onChange={value => onChange(index, 'description', value)}
                />
              </IssuesXcodesTableTd>
              <IssuesXcodesTableTd colSpan={1} paddingRight="1em">
                <Group gap="4" alignItems="center" justifyContent="flex-end" width="100%">
                  <IconButton
                    onClick={onAdd}
                    disabled={!isInEditMode}
                    colorScheme="actionSecondary"
                    height="25px"
                    minWidth="25px"
                    borderRadius="32px"
                  >
                    <MdAdd className="international-order-line__icon--orange" />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    disabled={!isInEditMode}
                    onClick={() => {
                      onDelete(index);
                    }}
                  >
                    <TrashOutlineIconSVG />
                  </IconButton>
                </Group>
              </IssuesXcodesTableTd>
            </Table.Row>
          ))}
        {issuesXcodes.length < 1 && (
          <Table.Row>
            <IssuesXcodesTableTd colSpan={5} paddingRight={0}>
              <Flex>
                <IconButton
                  marginLeft="auto"
                  onClick={onAdd}
                  disabled={!isInEditMode}
                  colorScheme="actionSecondary"
                  height="25px"
                  minWidth="25px"
                  borderRadius="32px"
                >
                  <MdAdd className="international-order-line__icon--orange" />
                </IconButton>
              </Flex>
            </IssuesXcodesTableTd>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

IssuesXcodes.propTypes = {
  issuesXcodesOption: PropTypes.arrayOf(Object),
  allIssuesXcodesOption: PropTypes.arrayOf(Object),
  isInEditMode: PropTypes.bool,
  issuesXcodes: PropTypes.instanceOf(Object),
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
};

export default IssuesXcodes;
